import type { FlexProps } from '@chakra-ui/react';
import { Flex, Icon, useTheme } from '@chakra-ui/react';
import { useTranslations } from '@ui/i18n';
import {
  getNftCollectionEndTime,
  getNftCollectionStartTime,
  useIsNftCollectionEnded,
} from '@ui/queries/types';
import { Clock, ClockEnded, ClockRunning } from '@veno-app/icons';
import type { NftCollection } from '@veno-app/sdk';
import type { FC } from 'react';
import { useEffect, useMemo, useState } from 'react';

type CountdownType = 'STARTS_IN' | 'ENDS_IN' | 'ENDED' | null;

type GardenNftCountdownProps = {
  collection: NftCollection;
  small?: boolean;
} & FlexProps;

const GardenNftCountdown: FC<GardenNftCountdownProps> = ({
  collection,
  small,
  ...flexProps
}) => {
  const t = useTranslations();
  const startTime = getNftCollectionStartTime(collection);
  const endTime = getNftCollectionEndTime(collection);
  const isEnded = useIsNftCollectionEnded(collection);

  const countdownType: CountdownType = useMemo(() => {
    const now = new Date().getTime();
    if (isEnded) return 'ENDED';
    if (startTime && startTime.getTime() > now) return 'STARTS_IN';
    if (endTime && endTime.getTime() > now) return 'ENDS_IN';
    if (endTime) return 'ENDED';
    return null;
  }, [endTime, startTime, isEnded]);

  const [countdownText, setCountdownText] = useState('');

  useEffect(() => {
    const updateCountdown = () => {
      if (countdownType === null) return;
      const now = new Date().getTime();
      let targetTime = null;

      if (startTime && startTime.getTime() > now) {
        targetTime = startTime;
      } else if (endTime && endTime.getTime() > now) {
        targetTime = endTime;
      }
      if (!targetTime) {
        setCountdownText('');
        return;
      }

      const distance = targetTime.getTime() - now;
      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
      );
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));

      let countdownDisplay = '';
      if (days > 0) countdownDisplay += `${days}d `;
      if (hours > 0) countdownDisplay += `${hours}h `;
      if (!small || (days === 0 && hours === 0))
        countdownDisplay += `${minutes}m`;

      setCountdownText(countdownDisplay);
    };

    const interval = setInterval(updateCountdown, 10000);
    updateCountdown();

    return () => clearInterval(interval);
  }, [startTime, endTime, countdownType, small]);

  const theme = useTheme();
  const [color, bgColor] = useMemo(() => {
    let color = theme.colors.success;
    if (countdownType === 'ENDED') color = theme.colors.error;
    if (countdownType === 'STARTS_IN') color = theme.colors.warning;
    return [color, `${color}3F`];
  }, [countdownType, theme.colors]);
  if (countdownType === null) return null;

  return (
    <Flex
      mt={2}
      py={1}
      pl={small ? 0 : 2}
      pr={small ? '2px' : 2}
      borderRadius="5px"
      border={`1px solid ${color}`}
      bg={bgColor}
      justify="center"
      align="center"
      textStyle="bodySmall"
      gap={small ? 0 : 1}
      {...flexProps}
    >
      {countdownType === 'STARTS_IN' && <Icon boxSize="24px" as={Clock} />}
      {countdownType === 'ENDS_IN' && <Icon boxSize="24px" as={ClockRunning} />}
      {countdownType === 'ENDED' && (
        <>
          <Icon boxSize="24px" as={ClockEnded} />
          {t('Ended')}
        </>
      )}
      {!small && countdownType !== 'ENDED' && (
        <>{countdownType === 'STARTS_IN' ? t('Starts in') : t('Ends in')}: </>
      )}
      {countdownType !== 'ENDED' && <> {countdownText}</>}
    </Flex>
  );
};

export default GardenNftCountdown;
